import React from 'react';
import { Eye, Download, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

/**
 * AdminInvoicesModal — admin-only modal that shows every paid invoice for a
 * single user, plus inline iframe preview + PDF download buttons.
 *
 * All state is owned by the parent (Admin.js) so this component stays a pure
 * view. Extracted from Admin.js to keep that file focused on tab routing.
 */
export const AdminInvoicesModal = ({
  open, user, invoices, loading, preview, onClose, onPreview, onClosePreview, onDownload,
}) => {
  if (!open) return null;

  const badge = (text, classes) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${classes}`}>{text}</span>
  );

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="user-invoices-modal"
    >
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-base text-gray-900">Tax Invoices — {user?.name || '—'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{user?.mobile} · {user?.email || '—'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-gray-400 text-sm">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading invoices…
          </div>
        ) : invoices.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No paid invoices yet for this user.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="p-2 text-left font-medium">Invoice #</th>
                  <th className="p-2 text-left font-medium">Type</th>
                  <th className="p-2 font-medium">Service Fee</th>
                  <th className="p-2 font-medium">GST 18%</th>
                  <th className="p-2 font-medium">Total</th>
                  <th className="p-2 font-medium">Date</th>
                  <th className="p-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const isActive = preview.invoiceNo === inv.invoice_no;
                  return (
                    <tr key={inv.order_id} className={`border-t border-gray-50 ${isActive ? 'bg-amber-50/60' : ''}`} data-testid={`user-invoice-row-${inv.order_id}`}>
                      <td className="p-2 font-mono text-xs">{inv.invoice_no}</td>
                      <td className="p-2">{badge(inv.type, inv.type === 'membership' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}</td>
                      <td className="p-2 text-right">Rs.{Number(inv.base_amount || 0).toFixed(2)}</td>
                      <td className="p-2 text-right">Rs.{Number(inv.gst_amount || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-semibold">Rs.{Number(inv.total_amount || 0).toFixed(2)}</td>
                      <td className="p-2 text-center text-xs">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</td>
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant={isActive ? 'default' : 'outline'}
                            onClick={() => onPreview(inv.order_id, inv.invoice_no)}
                            className={`h-8 rounded-lg text-xs ${isActive ? 'bg-amber-600 text-white hover:bg-amber-700' : ''}`}
                            data-testid={`user-invoice-preview-${inv.order_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> {isActive ? 'Previewing' : 'Preview'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => onDownload(inv.order_id, inv.invoice_no)}
                            className="h-8 rounded-lg text-xs bg-gray-900 text-white"
                            data-testid={`user-invoice-download-${inv.order_id}`}
                          >
                            <Download className="w-3.5 h-3.5 mr-1" /> PDF
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {preview.url && (
              <div className="mt-5 border border-gray-200 rounded-xl overflow-hidden" data-testid="invoice-preview-pane">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-100">
                  <span className="text-xs font-mono text-gray-700">{preview.invoiceNo}</span>
                  <button
                    onClick={onClosePreview}
                    className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"
                    data-testid="invoice-preview-close"
                  >
                    <X className="w-3.5 h-3.5" /> Close preview
                  </button>
                </div>
                <iframe
                  src={preview.url}
                  title={`Invoice ${preview.invoiceNo}`}
                  className="w-full"
                  style={{ height: '70vh', border: 'none' }}
                  data-testid="invoice-preview-iframe"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoicesModal;
